import { createContext, type ReactNode, useContext } from "react";

// Radix renders every floating layer (select listbox, menu, popover, tooltip,
// dialog) through a Portal that defaults to `document.body`. That is correct in
// the app — the layer must escape any `overflow: hidden` ancestor.
//
// It is wrong when a host renders the same tree twice under different themes:
// tokens live on `:root` / `.dark`, so a portal that lands on `document.body`
// picks up the <html> theme regardless of which subtree opened it. Storybook's
// side-by-side theme panes are the case that needs this.
//
// Default is `null` → `usePortalContainer()` returns undefined → Radix keeps its
// own default. Nothing changes for the app unless a provider is mounted.
const PortalContainerContext = createContext<HTMLElement | null>(null);

export function PortalContainerProvider({
  container,
  children,
}: {
  container: HTMLElement | null;
  children: ReactNode;
}) {
  return (
    <PortalContainerContext.Provider value={container}>{children}</PortalContainerContext.Provider>
  );
}

/** Element Radix portals should mount into; undefined means `document.body`. */
export function usePortalContainer(): HTMLElement | undefined {
  return useContext(PortalContainerContext) ?? undefined;
}
